import React from "react";

/**
 * Hero visual for MTS — a stylised world map with the MTS operator hub
 * positioned in Bali (Indonesia). From that origin, animated shipment
 * particles fan out to major destinations across every populated
 * continent.
 *
 * Composition:
 *  · Recognisable continent silhouettes (North America incl. Alaska + Baja,
 *    Greenland, South America, Europe with British Isles hint, Africa +
 *    Madagascar, Arabian peninsula, Asia mainland, Indian peninsula,
 *    Indochina, Japan, Korea, Indonesia archipelago (Sumatra, Java, Bali,
 *    Borneo, Sulawesi, New Guinea), Australia).
 *  · MTS hub anchored on Bali.
 *  · Eight destination markers with elegant uppercase labels covering
 *    every continent: Los Angeles, New York, São Paulo, London, Frankfurt,
 *    Dubai, Shanghai, Sydney.
 *  · Animated curved outbound routes from Bali to each destination.
 *
 * Monochrome, hairline grammar, no photography.
 */

const CONTINENTS = [
  // North America (main mass + Alaska tip)
  "M 60 90 Q 90 78 130 82 L 175 88 Q 210 92 235 108 Q 250 132 245 158 L 240 178 Q 218 208 192 222 L 175 235 L 170 250 Q 162 262 154 258 L 148 244 Q 138 235 128 232 L 105 226 Q 78 218 60 196 L 45 172 Q 40 142 45 115 Q 50 95 60 90 Z",
  // Baja California
  "M 155 232 L 168 260 Q 168 275 158 275 Q 150 268 148 252 Z",
  // Greenland
  "M 260 55 Q 288 55 302 68 L 306 88 Q 296 96 275 93 L 260 82 Q 254 68 260 55 Z",
  // South America
  "M 210 258 Q 238 256 258 270 Q 274 288 278 310 Q 282 340 276 370 L 265 400 Q 250 418 230 418 L 220 408 Q 208 388 204 360 Q 198 328 200 296 Q 200 270 210 258 Z",
  // Europe (with British Isles small mass and Scandinavia peninsula)
  "M 355 85 L 362 72 Q 380 66 396 78 L 408 96 Q 415 116 405 130 L 388 142 Q 410 150 422 160 Q 432 174 425 186 L 410 194 Q 385 200 370 192 Q 354 182 344 168 L 340 146 Q 336 118 348 100 Q 350 88 355 85 Z",
  // British Isles (small island group)
  "M 336 116 Q 344 116 346 124 L 344 138 Q 336 140 333 132 Q 331 122 336 116 Z",
  // Africa
  "M 388 172 Q 420 170 456 178 Q 476 186 486 208 L 496 234 Q 496 260 484 288 L 468 322 Q 452 354 432 368 L 414 374 Q 395 365 384 340 L 374 308 Q 362 272 364 236 L 372 208 Q 378 184 388 172 Z",
  // Madagascar
  "M 498 306 Q 508 306 510 316 L 506 336 Q 498 342 494 332 L 493 316 Z",
  // Arabian peninsula (Middle East)
  "M 460 178 Q 484 176 500 188 L 512 208 Q 512 230 500 240 L 480 240 Q 464 230 458 210 Q 455 190 460 178 Z",
  // Asia mainland
  "M 445 100 Q 495 88 555 88 L 615 92 Q 670 100 712 118 L 748 135 Q 764 154 758 176 L 752 202 Q 740 224 720 234 L 690 246 Q 640 254 596 250 L 566 246 Q 540 240 518 230 L 494 218 Q 476 206 466 190 L 460 165 Q 452 132 445 100 Z",
  // Indian subcontinent
  "M 522 220 Q 542 224 555 240 Q 560 262 552 282 L 542 290 Q 528 285 522 268 L 516 246 Q 515 232 522 220 Z",
  // Indochina peninsula
  "M 598 240 Q 614 258 618 282 L 614 306 Q 604 316 598 306 L 590 282 Q 586 258 598 240 Z",
  // Malay peninsula
  "M 616 300 Q 622 316 620 332 L 612 336 Q 606 322 608 306 Z",
  // Korea peninsula
  "M 682 152 L 692 172 Q 690 186 682 188 L 678 172 Z",
  // Japan (main island simplified)
  "M 700 142 Q 720 146 732 158 L 740 178 Q 736 194 726 198 L 712 194 Q 700 180 700 162 Z",
  // Sumatra
  "M 588 318 Q 615 322 632 335 L 646 350 Q 646 358 632 358 L 605 350 Q 588 340 588 318 Z",
  // Java (Bali sits on the eastern tip)
  "M 638 356 L 688 358 Q 702 362 702 368 L 696 372 Q 662 372 638 368 Q 634 362 638 356 Z",
  // Bali (small distinct island east of Java)
  "M 693 366 L 706 367 L 706 373 L 692 373 Z",
  // Borneo (Kalimantan)
  "M 645 318 Q 678 314 698 326 L 706 344 Q 700 354 686 352 L 660 346 Q 640 338 645 318 Z",
  // Sulawesi
  "M 708 332 L 722 338 L 720 356 L 708 360 Z",
  // New Guinea
  "M 735 350 Q 756 348 772 356 L 776 366 Q 770 372 754 370 L 738 362 Z",
  // Australia
  "M 660 382 Q 696 376 730 382 L 760 392 Q 770 406 760 420 L 725 428 Q 690 428 668 420 Q 650 406 660 382 Z",
];

export default function AsiaNetwork({ className = "" }) {
  // Hub anchored roughly between our Vietnam and Indonesia offices
  // (Java Sea area) — kept intentionally location-neutral.
  const hub = { x: 655, y: 305 };

  // Destinations — one or two per populated continent, using major cities
  const destinations = [
    { id: "lax", x: 80,  y: 175, name: "Los Angeles", labelDx: 0,   labelDy: -14, anchor: "middle" },
    { id: "nyc", x: 195, y: 168, name: "New York",    labelDx: 8,   labelDy: -10, anchor: "start" },
    { id: "sao", x: 245, y: 322, name: "São Paulo",   labelDx: 10,  labelDy: 5,   anchor: "start" },
    { id: "lhr", x: 358, y: 118, name: "London",      labelDx: -10, labelDy: -12, anchor: "end" },
    { id: "fra", x: 402, y: 138, name: "Frankfurt",   labelDx: 10,  labelDy: 16,  anchor: "start" },
    { id: "dxb", x: 488, y: 212, name: "Dubai",       labelDx: 10,  labelDy: 5,   anchor: "start" },
    { id: "sha", x: 655, y: 178, name: "Shanghai",    labelDx: 10,  labelDy: -8,  anchor: "start" },
    { id: "syd", x: 735, y: 415, name: "Sydney",      labelDx: 10,  labelDy: 5,   anchor: "start" },
  ];

  const path = (n) => {
    const mx = (hub.x + n.x) / 2;
    const arc = Math.max(35, Math.abs(hub.x - n.x) * 0.18);
    // For nearby destinations (e.g. Shanghai, Sydney) arc upward less
    const my = (hub.y + n.y) / 2 - arc;
    return `M ${hub.x} ${hub.y} Q ${mx} ${my} ${n.x} ${n.y}`;
  };

  return (
    <svg
      viewBox="0 0 800 440"
      className={className}
      role="img"
      aria-label="Stylised world map — MTS operator hub in Bali, Indonesia, connected by animated routes to Los Angeles, New York, São Paulo, London, Frankfurt, Dubai, Shanghai and Sydney"
    >
      <defs>
        <radialGradient id="mtsHazeV10" cx="88%" cy="82%" r="42%">
          <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="mtsLineOutV10" x1="1" x2="0" y1="1" y2="0">
          <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0.08" />
        </linearGradient>
        <pattern id="mtsDotsV10" width="16" height="16" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="#0a0a0a" fillOpacity="0.05" />
        </pattern>
        <radialGradient id="mtsFadeV10" cx="55%" cy="50%" r="65%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <mask id="mtsFadeMaskV10">
          <rect width="800" height="440" fill="url(#mtsFadeV10)" />
        </mask>
      </defs>

      {/* Dot texture, faded to centre */}
      <g mask="url(#mtsFadeMaskV10)">
        <rect width="800" height="440" fill="url(#mtsDotsV10)" />
      </g>

      {/* Latitude reference lines — very faint */}
      {[95, 175, 255, 335].map((y) => (
        <line
          key={y}
          x1="20"
          x2="780"
          y1={y}
          y2={y}
          stroke="#0a0a0a"
          strokeOpacity="0.05"
        />
      ))}

      {/* Continent silhouettes */}
      <g>
        {CONTINENTS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="#0a0a0a"
            fillOpacity="0.06"
            stroke="#0a0a0a"
            strokeOpacity="0.32"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        ))}
      </g>

      {/* Radial haze behind the Bali hub */}
      <circle cx={hub.x} cy={hub.y} r="220" fill="url(#mtsHazeV10)" />

      {/* Coverage rings around the Bali hub */}
      {[90, 160, 230, 300].map((r) => (
        <circle
          key={r}
          cx={hub.x}
          cy={hub.y}
          r={r}
          fill="none"
          stroke="#0a0a0a"
          strokeOpacity="0.09"
          strokeDasharray="2 8"
        />
      ))}

      {/* Outbound routes from Bali */}
      {destinations.map((n) => (
        <path
          key={n.id}
          id={`v10-dst-${n.id}`}
          d={path(n)}
          fill="none"
          stroke="url(#mtsLineOutV10)"
          strokeWidth="1"
        />
      ))}

      {/* Animated outbound shipment particles */}
      {destinations.map((n, i) => (
        <circle key={`p-${n.id}`} r="3" fill="#0a0a0a">
          <animateMotion
            dur={`${6 + i * 0.55}s`}
            repeatCount="indefinite"
            rotate="auto"
            begin={`${i * 0.7}s`}
          >
            <mpath href={`#v10-dst-${n.id}`} />
          </animateMotion>
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            keyTimes="0;0.15;0.85;1"
            dur={`${6 + i * 0.55}s`}
            repeatCount="indefinite"
            begin={`${i * 0.7}s`}
          />
        </circle>
      ))}

      {/* Destination markers + city labels */}
      {destinations.map((n) => (
        <g key={n.id}>
          <circle cx={n.x} cy={n.y} r="9" fill="#ffffff" stroke="#0a0a0a" strokeOpacity="0.28" />
          <circle cx={n.x} cy={n.y} r="3" fill="none" stroke="#0a0a0a" strokeWidth="1.3" />
          <text
            x={n.x + n.labelDx}
            y={n.y + n.labelDy}
            textAnchor={n.anchor}
            fill="#0a0a0a"
            fillOpacity="0.85"
            fontFamily="'Inter', sans-serif"
            fontSize="9.5"
            fontWeight="500"
            letterSpacing="0.16em"
            style={{ textTransform: "uppercase" }}
          >
            {n.name}
          </text>
        </g>
      ))}

      {/* MTS operator hub — anchored on Bali, Indonesia */}
      <g>
        <circle cx={hub.x} cy={hub.y} r="34" fill="#ffffff" stroke="#e5e5ea" />
        <circle cx={hub.x} cy={hub.y} r="24" fill="#0a0a0a" />
        <circle
          cx={hub.x}
          cy={hub.y}
          r="11"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.35"
          strokeWidth="1"
        />
        <text
          x={hub.x}
          y={hub.y}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#ffffff"
          fontFamily="'Inter', sans-serif"
          fontSize="11"
          fontWeight="600"
          letterSpacing="0.14em"
        >
          MTS
        </text>
      </g>
    </svg>
  );
}
